/**
 * <s-flavor-picker> — "Select a Product" flavor picker for the product page.
 *
 * Opens its dialog when the main product form's quantity becomes 2+, or when
 * the form is submitted with 2+ units and flavors are not yet chosen. The
 * shopper picks exactly that many units across the listed products; then
 * "Proceed to cart" adds ONE CART LINE PER CHOSEN FLAVOR (that product's real
 * variant and the chosen quantity) via Shopify Standard Actions, so inventory
 * moves per flavor. No line property is written. A selling plan chosen on the
 * page applies only to lines of the current product. After adding, the dialog
 * named in data-after-add opens (e.g. "Add More!"), else the cart opens.
 *
 * Data attributes: data-tier1/2/3 (cents, per unit for 1 / 2 / 3+ units),
 * data-current-product-id, data-after-add, data-error-message,
 * data-money-format, data-label-select, data-label-proceed,
 * data-label-progress ("[count] / [total] selected"),
 * data-label-title ("Choose [total] flavors").
 * Cards: [data-flavor][data-variant-id][data-product-id].
 */
const FORM_SELECTOR = 's-product-form[data-product-form] form';

class SFlavorPicker extends HTMLElement {
  #ac;
  #counts = new Map(); // variantId -> quantity
  #target = 0;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.dialog = this.querySelector('dialog');
    this.submitButton = this.querySelector('[data-flavor-submit]');

    document.addEventListener('change', (event) => this.#onChange(event), opts);
    document.addEventListener('submit', (event) => this.#onSubmit(event), { ...opts, capture: true });
    this.addEventListener('click', (event) => this.#onClick(event), opts);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #form() {
    return document.querySelector(FORM_SELECTOR);
  }

  #quantity() {
    const form = this.#form;
    if (!form) return 1;
    const value = Number.parseInt(String(new FormData(form).get('quantity') || '1'), 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  #selected() {
    let total = 0;
    for (const count of this.#counts.values()) total += count;
    return total;
  }

  #onChange(event) {
    const form = this.#form;
    if (!form || !(event.target instanceof HTMLInputElement) || event.target.form !== form) return;
    if (event.target.name !== 'quantity') return;
    const quantity = this.#quantity();
    if (quantity >= 2 && this.#selected() !== quantity) this.open(quantity);
  }

  #onSubmit(event) {
    const form = this.#form;
    if (event.target !== form) return;
    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"]')) return;

    const quantity = this.#quantity();
    if (quantity < 2) return;
    // 2+ units always go through the picker so each flavor becomes its own line.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#selected() === quantity) {
      this.#target = quantity;
      this.#proceed();
      return;
    }
    this.open(quantity);
  }

  open(quantity) {
    this.#target = quantity;
    if (this.#selected() > quantity) this.#counts.clear();
    this.#showError('');
    this.#render();
    if (!this.dialog.open) this.dialog.showModal();
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const card = target.closest('[data-flavor]');
    const key = card?.dataset.variantId;

    if (card && key && target.closest('[data-flavor-add]')) {
      if (this.#selected() < this.#target) this.#counts.set(key, 1);
      this.#render();
      card.querySelector('[data-flavor-plus]')?.focus();
    } else if (card && key && target.closest('[data-flavor-plus]')) {
      if (this.#selected() < this.#target) this.#counts.set(key, (this.#counts.get(key) || 0) + 1);
      this.#render();
    } else if (card && key && target.closest('[data-flavor-minus]')) {
      const next = (this.#counts.get(key) || 0) - 1;
      if (next > 0) {
        this.#counts.set(key, next);
        this.#render();
      } else {
        this.#counts.delete(key);
        this.#render();
        card.querySelector('[data-flavor-add]')?.focus();
      }
    } else if (target.closest('[data-flavor-submit]')) {
      this.#proceed();
    }
  }

  async #proceed() {
    if (this.#busy || this.#target < 1 || this.#selected() !== this.#target) return;
    const errorMessage = this.dataset.errorMessage || 'Could not add to cart.';
    if (!window.Shopify?.actions?.updateCart) {
      this.#showError(errorMessage);
      return;
    }

    const form = this.#form;
    const sellingPlanId = form ? String(new FormData(form).get('selling_plan') || '').trim() : '';
    const lines = [];
    for (const [variantId, quantity] of this.#counts.entries()) {
      const card = this.querySelector(`[data-flavor][data-variant-id="${CSS.escape(variantId)}"]`);
      const line = { merchandiseId: variantId, quantity };
      if (sellingPlanId && card?.dataset.productId === this.dataset.currentProductId) line.sellingPlanId = sellingPlanId;
      lines.push(line);
    }
    if (!lines.length) return;

    this.#busy = true;
    this.#showError('');
    this.submitButton?.setAttribute('aria-busy', 'true');
    this.submitButton?.classList.add('opacity-50', 'cursor-wait');
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines },
        { event: { context: 'product', detail: { source: 's-flavor-picker', productId: this.dataset.currentProductId } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) {
        this.#showError(message);
        return;
      }
      this.#counts.clear();
      this.#render();
      if (this.dialog.open) this.dialog.close();
      const after = this.dataset.afterAdd ? document.getElementById(this.dataset.afterAdd) : null;
      if (after instanceof HTMLDialogElement) {
        if (!after.open) after.showModal();
      } else {
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      this.#showError(error?.message || errorMessage);
    } finally {
      this.#busy = false;
      this.submitButton?.removeAttribute('aria-busy');
      this.submitButton?.classList.remove('opacity-50', 'cursor-wait');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-flavor-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  #unitCents(quantity) {
    const key = quantity >= 3 ? 'tier3' : quantity === 2 ? 'tier2' : 'tier1';
    return Number.parseInt(this.dataset[key] || '0', 10);
  }

  #money(cents) {
    const amount = (cents / 100).toFixed(2);
    const [whole, decimals] = amount.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const format = this.dataset.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
      if (kind === 'amount_no_decimals') return withCommas;
      if (kind === 'amount_with_comma_separator') return `${whole}.${decimals}`.replace('.', ',');
      return `${withCommas}.${decimals}`;
    });
  }

  #render() {
    const selected = this.#selected();
    const full = this.#target > 0 && selected >= this.#target;

    for (const card of this.querySelectorAll('[data-flavor]')) {
      const count = this.#counts.get(card.dataset.variantId) || 0;
      const add = card.querySelector('[data-flavor-add]');
      const stepper = card.querySelector('[data-flavor-stepper]');
      const plus = card.querySelector('[data-flavor-plus]');
      card.querySelector('[data-flavor-count]').textContent = String(count);
      add.hidden = count > 0;
      add.disabled = full;
      add.classList.toggle('opacity-40', full);
      stepper.hidden = count === 0;
      plus.disabled = full;
      card.classList.toggle('ring-2', count > 0);
      card.classList.toggle('ring-interactive', count > 0);
    }

    const replace = (text) => (text || '').replace('[count]', String(selected)).replace('[total]', String(this.#target));
    const subtitle = this.querySelector('[data-flavor-subtitle]');
    if (subtitle) subtitle.textContent = replace(this.dataset.labelTitle);
    const progress = this.querySelector('[data-flavor-progress]');
    if (progress) progress.textContent = replace(this.dataset.labelProgress);

    const totalWrap = this.querySelector('[data-flavor-total-wrap]');
    const totalEl = this.querySelector('[data-flavor-total]');
    const form = this.#form;
    const subscribing = form ? Boolean(String(new FormData(form).get('selling_plan') || '').trim()) : false;
    const unit = this.#unitCents(this.#target);
    if (totalWrap && totalEl) {
      totalWrap.hidden = subscribing || !unit || !this.#target;
      totalEl.textContent = unit ? this.#money(unit * this.#target) : '';
    }

    if (this.submitButton) {
      this.submitButton.textContent = full ? this.dataset.labelProceed : this.dataset.labelSelect;
      this.submitButton.setAttribute('aria-disabled', String(!full));
    }
  }
}

if (!customElements.get('s-flavor-picker')) {
  customElements.define('s-flavor-picker', SFlavorPicker);
}
